/**
 * Подписи раздела «Нормативная база» Сметчика: статусы редакций, виды базы,
 * сборники цен, роли строк состава нормы.
 *
 * Статусы редакции — свой жизненный цикл (разбор → проверка → действует →
 * архив), поэтому живут здесь, а не в общих словарях `smetaLabels`: там
 * намеренно нет статусов конкретных сущностей.
 */
import type {
  FsnbCoefficientResource,
  FsnbCoefficientScopeKind,
  FsnbCoefficientValues,
  FsnbEditionChange,
  FsnbEditionStatus,
  FsnbPriceDetails,
  FsnbResourceRole,
} from '@shared/api';
import { fmtMoney, fmtNum, type SmetaTone } from '@shared/lib/smetaLabels';

/** Виды базы ФСНБ-2022 в порядке сборников. */
export const BASE_TYPES = ['ГЭСН', 'ГЭСНм', 'ГЭСНр', 'ГЭСНп', 'ГЭСНмр'] as const;

export const BASE_TYPE_TITLES: Record<string, string> = {
  ГЭСН: 'строительные и специальные строительные работы',
  ГЭСНм: 'монтаж оборудования',
  ГЭСНр: 'ремонтно-строительные работы',
  ГЭСНп: 'пусконаладочные работы',
  ГЭСНмр: 'капитальный ремонт оборудования',
};

/** Сборники цен: ФСБЦ — из архива ФСНБ-2022, остальные — ФСНБ-2020 для ФЕР. */
export const PRICE_SOURCES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'fsbc', label: 'ФСБЦ', hint: 'ФСНБ-2022: цены ресурсов для ресурсно-индексного метода' },
  { value: 'fssc', label: 'ФССЦ', hint: 'ФСНБ-2020: материалы и оборудование для ФЕР' },
  { value: 'fsem', label: 'ФСЭМ', hint: 'ФСНБ-2020: эксплуатация машин для ФЕР' },
  { value: 'fsscpg', label: 'ФССЦпг', hint: 'ФСНБ-2020: перевозка грузов' },
];

export function sourceLabel(source: string): string {
  return PRICE_SOURCES.find((s) => s.value === source)?.label ?? source;
}

/** Категория ресурса из справочника («МашиныМеханизмы» — так в XML ФГИС ЦС). */
export function categoryLabel(category: string | null | undefined): string {
  if (!category) return '—';
  return category === 'МашиныМеханизмы' ? 'Машины и механизмы' : category;
}

/** Машина без цены: редакцию загрузили до раздела «Нормативная база», когда
 *  состав цены машин ФСБЦ ещё не сохранялся. */
export function machinePriceMissing(
  category: string | null | undefined,
  cost: number | null,
  details: FsnbPriceDetails | null | undefined,
): boolean {
  return (
    Boolean(category?.toLowerCase().includes('машин')) &&
    typeof cost !== 'number' &&
    typeof details?.PriceCostWithoutSalary !== 'number'
  );
}

export function editionStatusLabel(status: FsnbEditionStatus): string {
  switch (status) {
    case 'importing':
      return 'Разбирается';
    case 'pending_review':
      return 'На проверке';
    case 'active':
      return 'Действует';
    case 'archived':
      return 'В архиве';
    case 'failed':
      return 'Не загрузилась';
  }
}

export function editionStatusTone(status: FsnbEditionStatus): SmetaTone {
  switch (status) {
    case 'importing':
      return 'info';
    case 'pending_review':
      return 'warning';
    case 'active':
      return 'success';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function changeLabel(change: FsnbEditionChange): string {
  const work = change.entity === 'work';
  switch (change.change) {
    case 'added':
      return work ? 'Новая' : 'Новый';
    case 'removed':
      return work ? 'Исключена' : 'Исключён';
    default:
      return work ? 'Изменена' : 'Изменён';
  }
}

export function changeTone(change: FsnbEditionChange['change']): SmetaTone {
  switch (change) {
    case 'added':
      return 'success';
    case 'removed':
      return 'danger';
    default:
      return 'warning';
  }
}

/** Что изменилось в норме или ресурсе — словами. */
export function changedFieldLabel(field: string): string {
  switch (field) {
    case 'name':
      return 'наименование';
    case 'unit':
      return 'измеритель';
    case 'content':
      return 'состав работ';
    case 'resources':
      return 'ресурсы и расход';
    case 'nr_sp':
      return 'НР и СП';
    case 'cost':
      return 'цена';
    case 'opt_cost':
      return 'оптовая цена';
    case 'price_details':
      return 'состав цены машины';
    default:
      return field;
  }
}

/** Порядок групп состава — как в таблице нормы сборника. */
export const ROLE_ORDER: FsnbResourceRole[] = [
  'labor',
  'machinist_labor',
  'machine',
  'material',
  'equipment',
  'other',
];

export function roleTitle(role: FsnbResourceRole): string {
  switch (role) {
    case 'labor':
      return 'Затраты труда рабочих';
    case 'machinist_labor':
      return 'Затраты труда машинистов';
    case 'machine':
      return 'Машины и механизмы';
    case 'material':
      return 'Материалы';
    case 'equipment':
      return 'Оборудование';
    default:
      return 'Прочие ресурсы';
  }
}

/** «ГЭСНм 08-02-401-01» — шифр так, как его пишут в смете. */
export function fullCode(baseType: string, code: string): string {
  return `${baseType} ${code}`;
}

/** Цена ресурса одной строкой. У машин ФСБЦ цена разложена на части. */
export function priceText(
  cost: number | null,
  details: FsnbPriceDetails | null | undefined,
): string {
  if (typeof cost === 'number') return `${fmtMoney(cost)} ₽`;
  const without = details?.PriceCostWithoutSalary;
  const salary = details?.SalaryMach;
  if (typeof without === 'number') {
    const parts = [`${fmtMoney(without)} ₽ без ЗП машиниста`];
    if (typeof salary === 'number' && salary > 0) {
      parts.push(`ЗП машиниста ${fmtMoney(salary)} ₽`);
    }
    return parts.join(' · ');
  }
  return '—';
}

export function sizeLabel(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number') return '—';
  if (bytes < 1024 * 1024) return `${fmtNum(Math.round(bytes / 1024))} КБ`;
  return `${(bytes / (1024 * 1024)).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('ru-RU');
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/** Сумма значений объекта-счётчика ({ ГЭСН: 29976, ГЭСНм: 19208 } → 49184). */
export function total(counts: Record<string, number> | undefined): number {
  return Object.values(counts ?? {}).reduce((a, b) => a + b, 0);
}

/** Составляющие расхода нормы, к которым задан поправочный коэффициент, — в
 *  порядке таблицы нормы. */
export const COEF_PARTS = [
  { key: 'labor', short: 'Труд', title: 'Затраты труда рабочих' },
  { key: 'machine', short: 'Машины', title: 'Эксплуатация машин' },
  { key: 'machinist_labor', short: 'Труд маш.', title: 'Затраты труда машинистов' },
  { key: 'material', short: 'Материалы', title: 'Материальные ресурсы' },
] as const;

export type CoefPartKey = (typeof COEF_PARTS)[number]['key'];

/** Значение коэффициента к составляющей: «0,81», «= 1+0,25·N» или «—», если
 *  к этой составляющей коэффициент не применяется. */
export function coefValueText(values: Partial<FsnbCoefficientValues>, part: CoefPartKey): string {
  const formula = values[`${part}_formula` as keyof FsnbCoefficientValues] as
    string | null | undefined;
  if (formula) return `= ${formula.replace(/\*/g, '·')}`;
  const v = values[part];
  return typeof v === 'number' ? fmtNum(v) : '—';
}

/** Через что коэффициент привязан к норме. */
export function coefScopeLabel(scope: FsnbCoefficientScopeKind): string {
  switch (scope) {
    case 'work':
      return 'к норме';
    case 'table':
      return 'к таблице';
    case 'section':
      return 'к разделу';
    case 'collection':
      return 'к сборнику';
  }
}

/** «301/пр_15.05.2026» → «приказ № 301/пр от 15.05.2026». */
export function coefDecreeText(decree: string | null | undefined): string {
  if (!decree) return '—';
  const [number, date] = decree.split('_');
  return date ? `приказ № ${number} от ${date}` : `приказ № ${number}`;
}

/** Своё значение для ресурса одной строкой: «Материалы 0 — исключается». */
export function coefResourceText(r: FsnbCoefficientResource): string {
  return COEF_PARTS.filter((p) => typeof r[p.key] === 'number')
    .map((p) => {
      const v = r[p.key] as number;
      return `${p.short} ${fmtNum(v)}${v === 0 ? ' (исключается)' : ''}`;
    })
    .join(', ');
}
